import { dateTime, eur, num } from "@/lib/format";
import { useState } from "react";
import { UsageBars } from "@/components/UsageMeter";

export const Row = ({ k, v }) => (
  <div className="flex items-baseline justify-between gap-3 border-b border-slate-100 py-1.5 last:border-0">
    <span className="text-[12px] text-slate-500">{k}</span>
    <span className="mono text-[12px] text-slate-800">{v ?? "—"}</span>
  </div>
);

const USAGE_LABELS = {
  scans: "Zoekopdrachten",
  search_profiles: "Zoekprofielen",
  opportunities: "Handelskansen",
  watchlist: "Watchlist",
  ai_analyses: "AI-analyses",
  bpm_overrides: "BPM-aanpassingen",
  market_value_overrides: "Marktwaarde-aanpassingen",
  recalculations: "Herberekeningen",
};

export const AccountInfo = ({ detail, pricing }) => (
  <section data-testid="admin-account-info">
    <h3 className="label-xs">Accountinformatie</h3>
    <Row k="Contactpersoon" v={detail.user?.name} />
    <Row k="E-mailadres" v={detail.user?.email} />
    <Row k="Telefoon" v={detail.phone} />
    <Row k="Land" v={detail.country} />
    <Row k="Rol" v={detail.user?.tenant_role} />
    <Row k="Wachtwoord ingesteld" v={detail.user?.has_password ? "Ja" : "Nog niet"} />
    <Row k="Laatste login" v={dateTime(detail.user?.last_login_at)} />
    <Row k="Account aangemaakt" v={dateTime(detail.created_at)} />
    <Row k="Abonnement" v={pricing.plan_name} />
    <Row
      k="Maandprijs"
      v={`${eur(pricing.effective_price, { fallback: "—" })}${
        pricing.custom_price != null ? ` (standaard ${eur(pricing.base_price)})` : ""
      }`}
    />
    <Row k="Startdatum" v={dateTime(detail.subscription?.started_at)} />
    <Row k="Trial tot" v={dateTime(detail.subscription?.trial_ends_at)} />
    <Row k="Interne notitie" v={detail.internal_note} />
  </section>
);

export const UsagePanel = ({ usage }) => (
  <section data-testid="admin-account-usage">
    <h3 className="label-xs">Gebruik (echte data)</h3>
    {Object.entries(USAGE_LABELS).map(([key, text]) => (
      <Row key={key} k={text} v={num(usage?.[key])} />
    ))}
  </section>
);

export const UsageMonthPanel = ({ usage, onGrant, busy }) => {
  const [metric, setMetric] = useState("scans");
  const [amount, setAmount] = useState("");

  const submit = async () => {
    const value = Number(amount);
    if (!value || value <= 0) return;
    const ok = await onGrant(metric, value);
    if (ok) setAmount("");
  };

  return (
    <section data-testid="admin-usage-month">
      <h3 className="label-xs">Gebruik deze maand versus limiet</h3>
      <div className="mt-2">
        <UsageBars usage={usage} testid="admin-usage-bars" />
      </div>
      {!usage?.unlimited && (
        <div className="mt-3 border-t border-slate-100 pt-3">
          <div className="label-xs">Eenmalig extra ruimte deze maand</div>
          <div className="mt-1.5 flex flex-wrap items-center gap-2">
            <select
              value={metric}
              onChange={(e) => setMetric(e.target.value)}
              data-testid="admin-grant-metric"
              className="h-8 rounded-sm border border-slate-300 bg-white px-2 text-[12px]"
            >
              {(usage?.metrics || []).map((m) => (
                <option key={m.metric} value={m.metric}>
                  {m.label}
                </option>
              ))}
            </select>
            <input
              type="number"
              min="1"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="Aantal"
              data-testid="admin-grant-amount"
              className="h-8 w-24 rounded-sm border border-slate-300 bg-white px-2 text-[12px]"
            />
            <button
              type="button"
              disabled={busy || !amount}
              onClick={submit}
              className="btn-secondary"
              data-testid="admin-grant-submit"
            >
              Toekennen
            </button>
          </div>
        </div>
      )}
    </section>
  );
};

export const BillingPanel = ({ billing, subscription, onPaymentLink, busy }) => (
  <section data-testid="admin-billing-panel">
    <h3 className="label-xs">Facturatie (Stripe)</h3>
    <Row k="Stripe" v={billing?.stripe_configured ? `Actief (${billing.mode})` : "Niet ingesteld"} />
    <Row k="Klant-id" v={billing?.customer_id} />
    <Row k="Abonnement-id" v={billing?.subscription_id} />
    <Row k="Laatste betaling" v={dateTime(billing?.last_payment_at)} />
    <Row k="Volgende afschrijving" v={dateTime(billing?.next_payment_due_at)} />
    <Row
      k="Opzegging"
      v={billing?.cancel_at_period_end ? "Stopt einde periode" : "Loopt door"}
    />
    <Row k="Abonnementstatus" v={subscription?.status} />
    <button
      type="button"
      disabled={busy || !billing?.stripe_configured}
      onClick={onPaymentLink}
      className="btn-secondary mt-2"
      data-testid="admin-payment-link"
    >
      Betaallink genereren
    </button>
  </section>
);

export const AuditList = ({ entries }) => (
  <div className="mt-5 border-t border-slate-200 pt-4">
    <h3 className="label-xs">Auditlog van dit account</h3>
    <div className="mt-2 max-h-52 overflow-y-auto" data-testid="admin-account-audit">
      {(entries || []).map((entry) => (
        <div
          key={entry.id}
          className="flex items-baseline justify-between gap-3 border-b border-slate-100 py-1.5 text-[12px]"
        >
          <span className="text-slate-700">{entry.action}</span>
          <span className="text-slate-500">{entry.actor_email}</span>
          <span className="mono text-[11px] text-slate-500">{dateTime(entry.timestamp)}</span>
        </div>
      ))}
      {!(entries || []).length && (
        <p className="text-[12px] text-slate-500">Nog geen gebeurtenissen.</p>
      )}
    </div>
  </div>
);
